import { useState, type CSSProperties } from 'react';
import { Play } from 'lucide-react';
import type { Track } from '@/types';
import { artworkUrl, mediaUrl } from '@/lib/api';

type Props = {
  tracks: Track[];
  artwork?: string;
  artist?: string;
  activeVideoUrl?: string | null;
  onVideo: (url: string, track: Track) => void;
};

function isZacharyGrayArtist(name: string): boolean {
  return /^zachary gray$/i.test(name.trim());
}

/** `/stream/track/video/{id}` → `/stream/track/video/{id}/poster.jpg` */
function videoPosterPath(videoPath: string): string {
  const base = videoPath.trim().replace(/\/+$/, '');
  if (!base) return '';
  return `${base}/poster.jpg`;
}

function ShelfCard({
  track,
  url,
  active,
  albumArtSrc,
  artistName,
  onVideo,
}: {
  track: Track;
  url: string;
  active: boolean;
  albumArtSrc: string;
  artistName?: string;
  onVideo: (url: string, track: Track) => void;
}) {
  const posterSrc = mediaUrl(videoPosterPath(track.video || ''));
  const [src, setSrc] = useState(posterSrc || albumArtSrc);
  /** Poster width÷height — 4:3 cards stay shorter than the 16:9 base tile width. */
  const [aspect, setAspect] = useState(16 / 9);
  const cardStyle = {
    ['--mv-ar' as string]: String(aspect),
  } satisfies CSSProperties;

  return (
    <button
      type="button"
      className={`music-video-card${active ? ' is-active' : ''}`}
      style={cardStyle}
      aria-label={
        artistName
          ? `Play music video: ${track.name} by ${artistName}`
          : `Play music video: ${track.name}`
      }
      aria-pressed={active}
      onClick={() => onVideo(url, track)}
    >
      <span className="music-video-card-art" aria-hidden>
        {src ? (
          <img
            src={src}
            alt=""
            draggable={false}
            decoding="async"
            onLoad={(e) => {
              const img = e.currentTarget;
              const w = img.naturalWidth;
              const h = img.naturalHeight;
              if (w > 0 && h > 0) {
                const ratio = w / h;
                if (ratio > 0.2 && ratio < 5) setAspect(ratio);
              }
            }}
            onError={() => {
              if (albumArtSrc && src !== albumArtSrc) setSrc(albumArtSrc);
            }}
          />
        ) : (
          <span className="music-video-card-fallback" />
        )}
        <span className="music-video-card-play">
          <Play size={22} fill="currentColor" />
        </span>
      </span>
      <span className="music-video-card-meta">
        <span className="music-video-card-name">{track.name}</span>
        {artistName ? (
          <span className="music-video-card-artist">{artistName}</span>
        ) : null}
      </span>
    </button>
  );
}

/** Apple Music–style Music Videos shelf (after about text on desktop). */
export function MusicVideoShelf({
  tracks,
  artwork,
  artist,
  activeVideoUrl,
  onVideo,
}: Props) {
  const videos = tracks.filter((t) => Boolean(t.video?.trim()));
  if (!videos.length) return null;

  const artSrc = artwork ? artworkUrl(artwork) : '';
  const releaseArtist = artist?.trim() || 'Zachary Gray';

  return (
    <section className="music-video-shelf" aria-label="Music videos">
      <h2 className="music-video-shelf-title">Music Videos</h2>
      <div className="music-video-shelf-scroller">
        {videos.map((track) => {
          const url = mediaUrl(track.video!);
          const active = Boolean(activeVideoUrl && activeVideoUrl === url);
          // Track artist first; omit when the resolved name is Zachary Gray.
          const resolvedArtist =
            track.artist?.trim() || releaseArtist;
          const artistName = isZacharyGrayArtist(resolvedArtist)
            ? undefined
            : resolvedArtist;
          return (
            <ShelfCard
              key={`${track.id}-video`}
              track={track}
              url={url}
              active={active}
              albumArtSrc={artSrc}
              artistName={artistName}
              onVideo={onVideo}
            />
          );
        })}
      </div>
    </section>
  );
}
